import {
  Box,
  Typography,
  Paper,
  Button,
  CircularProgress,
  TextField,
} from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { approveDriverRequest, declineDriverRequest } from "services/driver";
import { useState } from "react";
import { useRTL } from "hooks/isRTL";
import CustomSnackbar from "Components/shared/CustomSnackbar";
import { mimeTypes } from "enums/shared";

/**
 * Displays file details for a driver change request and provides actions
 * to approve or decline the request.
 *
 * @component
 * @param {Object} props - The component props.
 * @param {string} props.requestId - The unique identifier for the driver change request.
 * @param {number} props.status - The current status of the request (Pending, approved, declined).
 * @returns {JSX.Element} The rendered `DriverRequestFile` component.
 */
const DriverRequestFile = ({ fileDetails, status, requestId }) => {
  const queryClient = useQueryClient();
  const [declineMessage, setDeclineMessage] = useState();

  const [isLoadingAction, setIsLoadingAction] = useState();
  const [snackbarDetails, setSnackbarDetails] = useState({
    message: "",
    severity: "success",
  });
  const { t } = useRTL();

  const handleSnackbarClose = () => {
    setSnackbarDetails({ message: "", severity: "success" });
  };

  /**
   * Approves the driver change request.
   */
  const { mutate: approveRequest } = useMutation({
    mutationFn: () => approveDriverRequest(requestId),
    onSuccess: () => {
      queryClient.invalidateQueries(["requestFilesDetails", requestId]);
      queryClient.invalidateQueries(["driverRequests"]);
      setSnackbarDetails({
        message: "Request approved successfully!",
        severity: "success",
      });
    },
    onError: (error) => {
      console.error(error);
      setSnackbarDetails({
        message: "Request approved successfully!",
        severity: "success",
      });
    },
    onSettled: () => setIsLoadingAction(false),
  });

  /**
   * Declines the driver change request with an optional explanation.
   */
  const { mutate: declineRequest } = useMutation({
    mutationFn: () =>
      declineDriverRequest({ requestId, explanaion: declineMessage }),
    onSuccess: () => {
      queryClient.invalidateQueries(["requestFilesDetails", requestId]);
      queryClient.invalidateQueries(["driverRequests"]);
      setSnackbarDetails({
        message: "Request declined successfully!",
        severity: "success",
      });
    },
    onError: (error) => {
      console.error(error);
      setSnackbarDetails({
        message: "Failed to decline the request.",
        severity: "error",
      });
    },
    onSettled: () => setIsLoadingAction(false),
  });

  /**
   * Downloads a file associated with the driver change request.
   *
   * @param {string} fileId - The unique identifier of the file.
   * @param {string} fileName - The name of the file to save as.
   */
  const handleFileDownload = (file) => {
    const mimeType = getMimeType(file.extention);
    const fileBlob = new Blob([file.content], { type: mimeType });
    const url = window.URL.createObjectURL(fileBlob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", file.name);
    document.body.appendChild(link);
    link.click();
    link.parentNode.removeChild(link);
  };

  const getMimeType = (ext) => {
    return mimeTypes[ext.toLowerCase()] || "application/octet-stream";
  };

  /**
   * Previews a file in a new browser tab.
   * @param {string} fileId The unique identifier of the file.
   * @param {string} fileExtension  The file's extension to determine its MIME type.
   * @param {string} fileName  The name of the file.
   */
  const handleFilePreview = (file) => {
    const mimeType = getMimeType(file.extention);
    const fileBlob = new Blob([file.content], { type: mimeType });
    const fileUrl = URL.createObjectURL(fileBlob);

    const newTab = window.open();
    if (newTab) {
      newTab.location.href = fileUrl;
    } else {
      console.error("Failed to open new tab");
    }
  };

  return (
    <>
      <Paper
        elevation={2}
        sx={{
          padding: "1.5rem",
          display: "flex",
          flexDirection: "column",
          gap: "1rem",
          minWidth: "300px",
          backgroundColor: "background.white",
          color: "background.dark",
        }}
      >
        <Typography variant="h6" fontWeight="bold">
          {t("files")}
        </Typography>
        {fileDetails?.length > 0 ? (
          fileDetails.map((file) => (
            <Box
              key={file.id}
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography
                sx={{
                  cursor: "pointer",
                  textDecoration: "underline",
                }}
                onClick={() => handleFilePreview(file)}
              >
                {file.name}
              </Typography>
              <Button
                variant="outlined"
                size="small"
                onClick={() => handleFileDownload(file)}
              >
                {t("download")}
              </Button>
            </Box>
          ))
        ) : (
          <Typography>No Files Available</Typography>
        )}

        {status === 0 && (
          <>
            {isLoadingAction ? (
              <CircularProgress size={30} />
            ) : (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "1rem",
                  mt: 2,
                }}
              >
                <TextField
                  value={declineMessage}
                  placeholder={t("rejectionExplanation")}
                  onChange={(e) => setDeclineMessage(e.target.value)}
                  multiline
                  rows={2}
                  sx={{ flex: 1 }}
                />
                <Box
                  sx={{
                    display: "flex",

                    gap: "1rem",
                  }}
                >
                  <Button
                    variant="contained"
                    color="success"
                    onClick={() => {
                      setIsLoadingAction(true);
                      approveRequest();
                    }}
                  >
                    {t("approve")}
                  </Button>
                  <Button
                    variant="outlined"
                    color="error"
                    disabled={!declineMessage}
                    onClick={() => {
                      setIsLoadingAction(true);
                      declineRequest();
                    }}
                  >
                    {t("decline")}
                  </Button>
                </Box>
              </Box>
            )}
          </>
        )}
      </Paper>

      <CustomSnackbar
        message={snackbarDetails.message}
        severity={snackbarDetails.severity}
        onClose={handleSnackbarClose}
      />
    </>
  );
};

export default DriverRequestFile;
